import { useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { ResponseVariant } from 'workflow/Insights/Insights'

import { getRegionalizedApiPerRegion } from 'common/api'
import { useGetPersonalizationLiveVariantsQuery } from 'features/unifiedFlow/api/queries/useGetPersonalizationLiveVariantsQuery'
import {
  useContent,
  useSelectedComponentElement,
} from 'features/unifiedFlow/contentPage/hooks'

import {
  calculateStatusCounts,
  filterByStatus,
  getVariantId,
  VariantStatus,
} from '../utils/variantUtils'

import { useCommonHandlers } from './useCommonHandlers'
import { ResponseVariantExtended } from './useManageOptimizeVariants'

type UseManagePersonalizeVariantsProps = {
  statusFilter: VariantStatus
  onSelectVariant: (variant: ResponseVariant) => void
  tweakSuccessVariantIds: string[]
  selectedPersonalizationId: string | undefined
}

export const useManagePersonalizeVariants = ({
  statusFilter,
  onSelectVariant,
  tweakSuccessVariantIds,
  selectedPersonalizationId,
}: UseManagePersonalizeVariantsProps) => {
  const { content, refetch: refetchContent } = useContent()
  const { data: selectedElement } = useSelectedComponentElement()
  const flags = useFlags()
  const queryClient = useQueryClient()
  const cachedVariantsRef = useRef<ResponseVariantExtended[]>([])
  const cachedStatusFilterRef = useRef<string>('')

  const personalizationRegion = useMemo(
    () =>
      selectedElement?.personalization_settings?.configuration
        ?.privacy_region || 'eu',
    [selectedElement?.personalization_settings?.configuration?.privacy_region]
  )

  const { data: liveVariantsData, isLoading: isLoadingLiveVariants } =
    useGetPersonalizationLiveVariantsQuery({
      personalizationId: selectedElement?.personalization_id,
      personalizationRegion,
      enabled: true,
    })

  const variants = useMemo(
    () => selectedElement?.personalized_variants || [],
    [selectedElement?.personalized_variants]
  )

  const dropFn = useCallback(
    async (variantId: string) => {
      if (!content || !selectedElement) {
        return
      }

      let personalizationRegion =
        selectedElement?.personalization_settings?.configuration
          ?.privacy_region || 'eu'
      if (flags.switchToRegionalizedData && personalizationRegion) {
        personalizationRegion = personalizationRegion.toLowerCase()
      }

      const isBaselineVariant = !!selectedElement.variants?.find(
        (v) => v.sl_id === variantId
      )

      const combinationId = selectedElement.personalized_variants?.find(
        (v) => v.variant_id === variantId
      )?.combinationId

      if (!combinationId && !isBaselineVariant) {
        throw new Error('Variant to update could not be found')
      }

      const approvalURLPath = isBaselineVariant
        ? `accounts/${content.account_id}/personalization/${selectedElement.personalization_id}/language/approval`
        : `accounts/${content.account_id}/personalization/${selectedElement.personalization_id}/combinations/${combinationId}/approval`

      const api = getRegionalizedApiPerRegion(personalizationRegion)
      await api.post(approvalURLPath, {
        drop_variant_ids: [variantId],
      })
    },
    [content, selectedElement, flags.switchToRegionalizedData]
  )

  const wsMessages = useMemo(() => content?.ws_messages, [content?.ws_messages])
  const contentId = useMemo(() => content?._id, [content?._id])
  const selectedElementId = useMemo(
    () => selectedElement?.element_id,
    [selectedElement?.element_id]
  )

  const variantsToBeApprovedFiltered = useMemo(() => {
    if (!contentId || !selectedElementId) {
      if (cachedStatusFilterRef.current === statusFilter) {
        return cachedVariantsRef.current
      }
      return []
    }

    if (statusFilter === 'live' && liveVariantsData) {
      const mappedLiveVariants: ResponseVariantExtended[] = liveVariantsData
        .map((variant) => {
          const id = variant.variantId
          let isLoading = false
          if (contentId && selectedElementId && id && wsMessages) {
            const tweakWsTopic = `content/${contentId}/element/${selectedElementId}/variant/${id}/tweakVariant`
            const rejectWsTopic = `content/${contentId}/element/${selectedElementId}/variant/${id}/rejectVariant`
            isLoading = wsMessages.some(
              (message) =>
                message.wsTopic === rejectWsTopic ||
                message.wsTopic === tweakWsTopic
            )
          }
          return {
            _id: id,
            text: variant.variantText,
            status: variant.variantStatus,
            isLoading,
            isRefreshed: tweakSuccessVariantIds.includes(id ?? ''),
            category: undefined,
            isHighlighted: false,
            isHoldout: variant.isHoldout,
            sentiments: variant.sentiments,
            ownsl: variant.isHoldout,
            combinationId: variant.combinationId,
          } as any as ResponseVariantExtended
        })
        .filter((variant) => {
          if (!selectedPersonalizationId) {
            return true
          }
          return variant.combinationId === selectedPersonalizationId
        })

      const controlVariants: ResponseVariantExtended[] = []
      const regularVariants: ResponseVariantExtended[] = []

      mappedLiveVariants.forEach((variant) => {
        if (variant.isHoldout === true) {
          controlVariants.push(variant)
        } else {
          regularVariants.push(variant)
        }
      })

      controlVariants.sort((a, b) => a._id.localeCompare(b._id))
      regularVariants.sort((a, b) => a._id.localeCompare(b._id))

      const result = [...controlVariants, ...regularVariants]
      cachedVariantsRef.current = result
      cachedStatusFilterRef.current = statusFilter
      return result
    }

    const mappedVariants: ResponseVariantExtended[] = variants
      .filter((variant) => getVariantId(variant))
      .map((variant) => {
        const id = getVariantId(variant)
        let isLoading = false
        if (contentId && selectedElementId && id && wsMessages) {
          const tweakWsTopic = `content/${contentId}/element/${selectedElementId}/variant/${id}/tweakVariant`
          const rejectWsTopic = `content/${contentId}/element/${selectedElementId}/variant/${id}/rejectVariant`
          isLoading = wsMessages.some(
            (message) =>
              message.wsTopic === rejectWsTopic ||
              message.wsTopic === tweakWsTopic
          )
        }
        return {
          ...variant,
          _id: id,
          text: variant.text,
          status: variant.status,
          isLoading: variant.isLoading || isLoading,
          isRefreshed: tweakSuccessVariantIds.includes(id ?? ''),
          category: undefined,
          isHighlighted: false,
          isHoldout: variant.control,
          sentiments: variant.sentiments,
          ownsl: false,
          combinationId: variant.combinationId,
        } as any as ResponseVariantExtended
      })
      .filter((variant) => filterByStatus(variant, statusFilter))
      .filter((variant) => {
        if (!selectedPersonalizationId) {
          return true
        }
        return variant.combinationId === selectedPersonalizationId
      })

    const controlVariants: ResponseVariantExtended[] = []
    const regularVariants: ResponseVariantExtended[] = []

    mappedVariants.forEach((variant) => {
      if (variant.isHoldout === true) {
        controlVariants.push(variant)
      } else {
        regularVariants.push(variant)
      }
    })

    controlVariants.sort((a, b) => a._id.localeCompare(b._id))
    regularVariants.sort((a, b) => a._id.localeCompare(b._id))

    const result = [...controlVariants, ...regularVariants]
    cachedVariantsRef.current = result
    cachedStatusFilterRef.current = statusFilter
    return result
  }, [
    variants,
    statusFilter,
    tweakSuccessVariantIds,
    wsMessages,
    contentId,
    selectedElementId,
    selectedPersonalizationId,
    liveVariantsData,
  ])

  const onApproveSuccess = useCallback(
    (_variantId: string) => {
      refetchContent()

      queryClient.invalidateQueries({
        queryKey: [
          'personalization-live-variants',
          content?.account_id,
          selectedElement?.personalization_id,
          personalizationRegion,
        ],
      })
    },
    [
      refetchContent,
      queryClient,
      content?.account_id,
      selectedElement?.personalization_id,
      personalizationRegion,
    ]
  )

  const onDropSuccess = useCallback(
    (_variantId: string) => {
      refetchContent()

      queryClient.invalidateQueries({
        queryKey: [
          'personalization-live-variants',
          content?.account_id,
          selectedElement?.personalization_id,
          personalizationRegion,
        ],
      })
    },
    [
      refetchContent,
      queryClient,
      content?.account_id,
      selectedElement?.personalization_id,
      personalizationRegion,
    ]
  )

  const onBulkApproveSuccess = useCallback(
    (_variantIds: string[]) => {
      refetchContent()

      queryClient.invalidateQueries({
        queryKey: [
          'personalization-live-variants',
          content?.account_id,
          selectedElement?.personalization_id,
          personalizationRegion,
        ],
      })
    },
    [
      refetchContent,
      queryClient,
      content?.account_id,
      selectedElement?.personalization_id,
      personalizationRegion,
    ]
  )

  const onBulkDropSuccess = useCallback(
    (_variantIds: string[]) => {
      refetchContent()

      queryClient.invalidateQueries({
        queryKey: [
          'personalization-live-variants',
          content?.account_id,
          selectedElement?.personalization_id,
          personalizationRegion,
        ],
      })
    },
    [
      refetchContent,
      queryClient,
      content?.account_id,
      selectedElement?.personalization_id,
      personalizationRegion,
    ]
  )

  const sharedHook = useCommonHandlers({
    variantsToBeApprovedFiltered,
    onSelectVariant,
    content,
    selectedElement,
    dropFn,
    onApproveSuccess,
    onDropSuccess,
    onBulkApproveSuccess,
    onBulkDropSuccess,
  })

  const statusCounts = useMemo(() => {
    const counts = calculateStatusCounts(variants)

    if (liveVariantsData) {
      const liveCount = liveVariantsData.filter(
        (v) => v.variantStatus === 'live' || v.variantStatus === 'approved'
      ).length
      counts.live = liveCount
    }

    return counts
  }, [variants, liveVariantsData])

  return {
    ...sharedHook,
    variantsToBeApprovedFiltered,
    isLoading: statusFilter === 'live' ? isLoadingLiveVariants : false,
    fetchError: false,
    statusCounts,
  }
}
